import { AccountSwitcher } from '@agentic-flow-studio/shared/components/clinic/AccountSwitcher';
import { TriageCustomerSidebar } from '@agentic-flow-studio/shared/components/clinic/TriageCustomerSidebar';
import { TriageCustomerView } from '@agentic-flow-studio/shared/components/clinic/TriageCustomerView';
import { TriageDensityToggle } from '@agentic-flow-studio/shared/components/clinic/TriageDensityToggle';
import { TriageInboxView } from '@agentic-flow-studio/shared/components/clinic/TriageInboxView';
import { TriagePriorityView } from '@agentic-flow-studio/shared/components/clinic/TriagePriorityView';
import { TriageViewSwitcher } from '@agentic-flow-studio/shared/components/clinic/TriageViewSwitcher';
import { SplitPaneLayout } from '@agentic-flow-studio/shared/components/layout/SplitPaneLayout';
import { CreateTaskDialog } from '@agentic-flow-studio/shared/components/tasks/CreateTaskDialog';
import { TaskSideloadContent } from '@agentic-flow-studio/shared/components/tasks/TaskSideloadContent';
import { useSearchPreservingNavigate } from '@agentic-flow-studio/shared/hooks/useSearchPreservingNavigate';
import { useTriageDensity } from '@agentic-flow-studio/shared/hooks/useTriageDensity';
import { useTriageFilters } from '@agentic-flow-studio/shared/hooks/useTriageFilters';
import { useTriageView } from '@agentic-flow-studio/shared/hooks/useTriageView';
import type { TriageFilters } from '@agentic-flow-studio/shared/lib/clinic-logic';
import {
  getCategoriesForLabel,
  threadDisplayTitle,
} from '@agentic-flow-studio/shared/lib/clinic-logic';
import { cn } from '@agentic-flow-studio/shared/lib/utils';
import type { EmailThreadMetadata } from '@agentic-flow-studio/shared/types/email';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Filter, RefreshCw, Settings, SortAsc, SortDesc } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useArchiveClinicThread,
  useClinicCategories,
  useClinicDomainWeights,
  useClinicMailAccounts,
  useClinicSummary,
  useClinicThreads,
  useCreateClinicTask,
  useResolvedTriageConfig,
  useWorkspaceMembers,
} from '@/hooks/useClinic';
import { clinicApi } from '@/utils/api';
import { trpc } from '@/utils/trpc';

export default function TriageDashboard() {
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [activeTaskThread, setActiveTaskThread] = useState<EmailThreadMetadata | null>(null);
  const navigate = useSearchPreservingNavigate();
  const location = useLocation();
  const { workspaceId, orgId, taskId } = useParams<{
    workspaceId: string;
    orgId: string;
    taskId: string;
  }>();
  const parsedTaskId = taskId ? parseInt(taskId, 10) : undefined;

  const isEditMode = location.pathname.includes('/edit');

  const { view, setView, selectedCustomerId, setSelectedCustomerId } = useTriageView();
  const { density, setDensity } = useTriageDensity();
  const isDense = density === 'dense';

  const archiveThreadMutation = useArchiveClinicThread();

  const meQuery = trpc.auth.me.useQuery();
  const activeWorkspace = meQuery.data?.workspaces.find((w) => w.id.toString() === workspaceId);
  const isReadOnly = activeWorkspace?.role === 'viewer';

  const createTaskMutation = useCreateClinicTask();
  const queryClient = useQueryClient();

  const taskQuery = trpc.tasks.getById.useQuery({ id: parsedTaskId! }, { enabled: !!parsedTaskId });

  const updateMutation = trpc.tasks.update.useMutation({
    onSuccess: () => {
      toast.success('Task updated');
      queryClient.invalidateQueries({ queryKey: ['clinic'] });
      taskQuery.refetch();
      if (parsedTaskId) {
        navigate(`/org/${orgId}/workspace/${workspaceId}/triage/tasks/${parsedTaskId}`);
      } else {
        navigate(`/org/${orgId}/workspace/${workspaceId}/triage`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to update task: ${error.message}`);
    },
  });

  const deleteMutation = trpc.tasks.delete.useMutation({
    onSuccess: () => {
      toast.success('Task deleted');
      queryClient.invalidateQueries({ queryKey: ['clinic'] });
      navigate(`/org/${orgId}/workspace/${workspaceId}/triage`);
    },
    onError: (error) => {
      toast.error(`Failed to delete task: ${error.message}`);
    },
  });

  const handleDeleteTask = (id: number) => {
    if (isReadOnly) return;
    if (confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      deleteMutation.mutate({ id });
    }
  };

  const syncMutation = trpc.gmail.triggerPipeline.useMutation({
    onSuccess: () => {
      toast.success('Sync started. New threads appear here in a few moments.');
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['clinic'] }), 5000);
    },
    onError: (err) => {
      toast.error(`Failed to start sync: ${err.message}`);
    },
  });

  /**
   * Re-analyse a single thread the model previously failed on. The row already
   * exists (ingest no longer depends on analysis — ADR-0003), so this decorates it
   * in place rather than fetching anything new.
   */
  const retryAnalysisMutation = useMutation({
    mutationFn: (threadId: string) => clinicApi.ingestThread(threadId),
    onSuccess: () => {
      toast.success('Thread analyzed');
      queryClient.invalidateQueries({ queryKey: ['clinic'] });
    },
    onError: (err: Error) => {
      toast.error(`Analysis failed: ${err.message}`);
    },
  });
  const handleRetryAnalysis = (threadId: string) => retryAnalysisMutation.mutate(threadId);

  const { data: accounts = [] } = useClinicMailAccounts();
  const { data: members = [] } = useWorkspaceMembers();
  const { data: threads = [], isLoading: isThreadsLoading } = useClinicThreads(selectedAccounts);
  const { data: summary = [] } = useClinicSummary(selectedAccounts);
  const { data: categories = [] } = useClinicCategories();
  const { data: domainWeights = {} } = useClinicDomainWeights();
  const { data: { labelMap = {}, labelWeights = {}, categoryMappings: mappings = [] } = {} } =
    useResolvedTriageConfig();

  const { filters, filteredThreads, setFilter } = useTriageFilters(
    threads,
    labelWeights,
    domainWeights,
    categories,
    mappings
  );

  const resolvedCount = threads.filter((t) => !!t.resolvedAt).length;

  const getLabelName = (id: string) => {
    if (id === 'YELLOW_STAR') return '⭐';
    return labelMap[id] || id;
  };

  const availableLabels = Array.from(new Set(threads.flatMap((t) => t.labels || [])))
    .filter(
      (l) =>
        typeof l === 'string' &&
        !l.startsWith('CATEGORY_') &&
        !['INBOX', 'UNREAD', 'IMPORTANT', 'SENT', 'DRAFT', 'SPAM', 'TRASH', 'STARRED'].includes(l)
    )
    .sort() as string[];

  const handleArchive = (threadId: string) => {
    archiveThreadMutation.mutate(threadId, {
      onSuccess: () => {
        toast.success('Thread archived successfully');
      },
      onError: (err) => {
        toast.error(`Failed to archive thread: ${err.message}`);
      },
    });
  };

  const handleDelegate = () => {
    /* TODO */
  };

  const handleCreateTask = (threadId: string) => {
    const thread = threads.find((t) => t.threadId === threadId);
    setActiveTaskThread(thread || null);
  };

  const handleTaskClick = (taskId: number) => {
    navigate(`/org/${orgId}/workspace/${workspaceId}/triage/tasks/${taskId}`);
  };

  const handleCardClick = (threadId: string) => {
    navigate(`/org/${orgId}/workspace/${workspaceId}/triage/${threadId}`);
  };

  return (
    <SplitPaneLayout
      isOpen={!!parsedTaskId}
      onClose={() => navigate(`/org/${orgId}/workspace/${workspaceId}/triage`)}
      mainPaneClassName="overflow-hidden p-0 h-full"
      sidePanelContent={
        parsedTaskId && taskQuery.data ? (
          <TaskSideloadContent
            task={taskQuery.data}
            workspaceMembers={members}
            isSubmitting={updateMutation.isPending}
            isDeleting={deleteMutation.isPending}
            initialMode={isEditMode ? 'edit' : 'view'}
            onClose={() => navigate(`/org/${orgId}/workspace/${workspaceId}/triage`)}
            onDelete={() => parsedTaskId && handleDeleteTask(parsedTaskId)}
            onSubmit={(data) => {
              if (!parsedTaskId) return;
              updateMutation.mutate({ id: parsedTaskId, ...data });
            }}
            isReadOnly={isReadOnly}
          />
        ) : null
      }
    >
      <div className="flex h-full w-full bg-muted">
        {/* Filterable Sidebar */}
        <aside className="w-64 border-r border-border/50 p-6 flex flex-col gap-8 bg-card/30 backdrop-blur-sm shrink-0 overflow-y-auto h-full scrollbar-thin">
          {view === 'customer' ? (
            <TriageCustomerSidebar
              threads={threads}
              selectedCustomerId={selectedCustomerId}
              onSelect={setSelectedCustomerId}
            />
          ) : (
            <>
              <div>
                <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                  <Filter className="h-4 w-4" /> Filters
                </h3>
                <div className="space-y-6">
                  <AccountSwitcher
                    accounts={accounts}
                    selectedAccounts={selectedAccounts}
                    onToggle={(email) => {
                      if (selectedAccounts.includes(email))
                        setSelectedAccounts(selectedAccounts.filter((e) => e !== email));
                      else setSelectedAccounts([...selectedAccounts, email]);
                    }}
                    onClear={() => setSelectedAccounts([])}
                  />

                  <FilterGroup
                    label="Difficulty"
                    options={['all', 'Hard', 'Moderate', 'Easy']}
                    value={filters.difficulty}
                    onChange={(v) => setFilter('difficulty', v as TriageFilters['difficulty'])}
                  />

                  <FilterGroup
                    label="Domain"
                    options={
                      [
                        'all',
                        ...Array.from(new Set(threads.map((t) => t.domainCategory || ''))).filter(
                          Boolean
                        ),
                      ] as string[]
                    }
                    value={filters.domain}
                    onChange={(v) => setFilter('domain', v as TriageFilters['domain'])}
                  />

                  <FilterGroup
                    label="Labels"
                    options={['all', ...availableLabels] as string[]}
                    value={filters.label}
                    onChange={(v) => setFilter('label', v)}
                    formatLabel={getLabelName}
                  />

                  <FilterGroup
                    label="Category"
                    options={['all', ...categories.map((c) => c.id.toString())]}
                    value={filters.category === 'all' ? 'all' : filters.category.toString()}
                    onChange={(v) => setFilter('category', v === 'all' ? 'all' : parseInt(v, 10))}
                    formatLabel={(id) => categories.find((c) => c.id.toString() === id)?.name || id}
                  />

                  <div className="pt-2 border-t border-border/20">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={filters.hasDeadline}
                        onChange={(e) => setFilter('hasDeadline', e.target.checked)}
                        className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                      />
                      <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                        Has Deadline
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold flex items-center gap-2">Sort by</h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() =>
                      setFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc')
                    }
                  >
                    {filters.sortOrder === 'desc' ? (
                      <SortDesc className="h-4 w-4" />
                    ) : (
                      <SortAsc className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <div className="space-y-4">
                  <FilterGroup
                    label=""
                    options={['importance', 'deadline', 'newest', 'effort']}
                    value={filters.sortBy}
                    onChange={(v) => setFilter('sortBy', v as TriageFilters['sortBy'])}
                    // calm-interface-ignore: capitalises the first letter only, which is sentence case
                    formatLabel={(v) => v.charAt(0).toUpperCase() + v.slice(1)}
                  />
                </div>
              </div>
            </>
          )}

          <div className="mt-auto pt-8 border-t border-border/20">
            <Button
              variant="outline"
              className="w-full gap-2 text-xs h-9 justify-start"
              onClick={() => navigate(`/org/${orgId}/workspace/${workspaceId}/settings/clinic`)}
            >
              <Settings className="h-4 w-4" />
              Clinic Settings
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main
          className={cn(
            'flex-1 overflow-y-auto relative h-full scrollbar-thin',
            isDense ? 'p-4' : 'p-8'
          )}
        >
          {/* Dense rows trade the reading column for width — they have far more
 horizontal content to lay out than a card does. */}
          <div className={cn('mx-auto', isDense ? 'max-w-none space-y-4' : 'max-w-4xl space-y-8')}>
            {isReadOnly && (
              <div className="bg-muted-foreground/10 border border-border text-foreground p-4 rounded-xl flex items-start gap-3">
                <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold">Read-only Workspace</p>
                  <p className="opacity-90">
                    You have read-only access to this workspace. Modifying tasks, delegating
                    threads, and archiving items is disabled.
                  </p>
                </div>
              </div>
            )}
            {/* Standup Header */}
            <section className={isDense ? 'space-y-2' : 'space-y-4'}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1
                    className={cn('font-semibold tracking-tight', isDense ? 'text-xl' : 'text-3xl')}
                  >
                    Triage Dashboard
                  </h1>
                  <p className={cn('text-muted-foreground', isDense ? 'text-xs' : 'mt-1')}>
                    You have {isThreadsLoading ? '...' : threads.length} prioritized threads in
                    Triage across {new Set(summary.map((s) => s.domainCategory)).size} domains.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer group whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={filters.showResolved}
                      onChange={(e) => setFilter('showResolved', e.target.checked)}
                      className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                    />
                    <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                      Show Resolved
                    </span>
                    {!filters.showResolved && resolvedCount > 0 && (
                      <span className="text-xs text-muted-foreground/70">({resolvedCount})</span>
                    )}
                  </label>
                  <TriageViewSwitcher value={view} onChange={setView} />
                  <TriageDensityToggle value={density} onChange={setDensity} />
                  {!isReadOnly && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => syncMutation.mutate({})}
                      disabled={syncMutation.isPending}
                    >
                      <RefreshCw
                        className={cn('h-3.5 w-3.5', syncMutation.isPending && 'animate-spin')}
                      />
                      Sync Now
                    </Button>
                  )}
                </div>
              </div>

              {/* The KPI blocks are the tallest thing on the page and duplicate the
 category chips below — dense mode drops them for the chips alone. */}
              {!isDense && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {categories.slice(0, 3).map((cat, i) => (
                    <KPIBlock
                      key={cat.id}
                      label={cat.name}
                      count={
                        threads.filter((t) =>
                          (t.labels || []).some((l: string) =>
                            getCategoriesForLabel(t, mappings, categories, l).some(
                              (c) => c.id === cat.id
                            )
                          )
                        ).length
                      }
                      color={
                        i === 0
                          ? 'bg-muted-foreground'
                          : i === 1
                            ? 'bg-muted-foreground'
                            : 'bg-muted-foreground'
                      }
                    />
                  ))}
                  {categories.length === 0 && (
                    <>
                      <KPIBlock
                        label="Hard Tasks"
                        count={summary
                          .filter((s) => s.difficultyLevel === 'Hard')
                          .reduce((acc, s) => acc + s.count, 0)}
                        color="bg-muted-foreground"
                      />
                      <KPIBlock
                        label="Quick Approvals"
                        count={summary
                          .filter((s) => s.actionType === 'Quick Approval')
                          .reduce((acc, s) => acc + s.count, 0)}
                        color="bg-muted-foreground"
                      />
                      <KPIBlock
                        label="Support Requests"
                        count={summary
                          .filter((s) => s.domainCategory === 'Support')
                          .reduce((acc, s) => acc + s.count, 0)}
                        color="bg-muted-foreground"
                      />
                    </>
                  )}
                </div>
              )}

              {categories.length > 0 && (
                <div className={cn('flex flex-wrap gap-2', !isDense && 'pt-2')}>
                  {categories.map((cat) => {
                    const threadsInCat = threads.filter((t) =>
                      (t.labels || []).some((l: string) =>
                        getCategoriesForLabel(t, mappings, categories, l).some(
                          (c) => c.id === cat.id
                        )
                      )
                    );
                    if (threadsInCat.length === 0) return null;
                    return (
                      <Badge
                        key={cat.id}
                        variant="outline"
                        className="px-3 py-1 bg-white border-border/50 gap-2"
                      >
                        <div
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="font-medium">{cat.name}</span>
                        <span className="text-muted-foreground ml-1">{threadsInCat.length}</span>
                      </Badge>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Triage Cards */}
            <section className="space-y-4">
              {view === 'inbox' ? (
                <TriageInboxView
                  threads={filteredThreads}
                  isLoading={isThreadsLoading}
                  density={density}
                  labelMap={labelMap}
                  mappings={mappings}
                  categories={categories}
                  workspaceMembers={members}
                  onArchive={handleArchive}
                  onDelegate={handleDelegate}
                  onCreateTask={handleCreateTask}
                  onTaskClick={handleTaskClick}
                  onCardClick={handleCardClick}
                  onRetryAnalysis={handleRetryAnalysis}
                  isReadOnly={isReadOnly}
                />
              ) : view === 'customer' ? (
                <TriageCustomerView
                  threads={filteredThreads}
                  selectedCustomerId={selectedCustomerId}
                  isLoading={isThreadsLoading}
                  density={density}
                  labelMap={labelMap}
                  mappings={mappings}
                  categories={categories}
                  workspaceMembers={members}
                  onArchive={handleArchive}
                  onDelegate={handleDelegate}
                  onCreateTask={handleCreateTask}
                  onTaskClick={handleTaskClick}
                  onCardClick={handleCardClick}
                  onRetryAnalysis={handleRetryAnalysis}
                  isReadOnly={isReadOnly}
                />
              ) : (
                <TriagePriorityView
                  threads={filteredThreads}
                  isLoading={isThreadsLoading}
                  density={density}
                  labelMap={labelMap}
                  mappings={mappings}
                  categories={categories}
                  workspaceMembers={members}
                  onArchive={handleArchive}
                  onDelegate={handleDelegate}
                  onCreateTask={handleCreateTask}
                  onTaskClick={handleTaskClick}
                  onCardClick={handleCardClick}
                  onRetryAnalysis={handleRetryAnalysis}
                  isReadOnly={isReadOnly}
                />
              )}
            </section>
          </div>
        </main>
      </div>

      <CreateTaskDialog
        isOpen={!!activeTaskThread}
        onClose={() => setActiveTaskThread(null)}
        onSubmit={(data) => {
          if (!activeTaskThread) return;
          createTaskMutation.mutate(
            {
              threadId: activeTaskThread.threadId,
              title: data.label,
              description: data.content,
              assignedTo: data.assignedTo ?? undefined,
              // The clinic REST endpoint takes an ISO date; the shared payload carries epoch seconds.
              dueAt: data.dueAt ? new Date(data.dueAt * 1000).toISOString() : undefined,
              priority: data.priority,
              contactId: data.contactId ?? undefined,
            },
            {
              onSuccess: () => {
                toast.success('Task created');
                setActiveTaskThread(null);
              },
              onError: (err) => {
                toast.error(`Failed to create task: ${err.message}`);
              },
            }
          );
        }}
        initialTitle={activeTaskThread ? `${threadDisplayTitle(activeTaskThread)}` : ''}
        initialDescription={activeTaskThread?.coreAsk ?? ''}
        initialContactId={activeTaskThread?.contact?.id ?? null}
        workspaceMembers={members}
        isSubmitting={createTaskMutation.isPending}
      />
    </SplitPaneLayout>
  );
}

function FilterGroup({
  label,
  options,
  value,
  onChange,
  formatLabel,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
  formatLabel?: (v: string) => string;
}) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium text-muted-foreground">{label}</h4>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={cn(
              'px-3 py-1.5 rounded-full text-xs font-medium transition-all border',
              value === opt
                ? 'bg-primary text-primary-foreground border-primary '
                : 'bg-background border-border hover:border-primary/50 text-muted-foreground'
            )}
          >
            {opt === 'all' ? opt : formatLabel ? formatLabel(opt) : opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function KPIBlock({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div className="bg-card border border-border/50 rounded-xl p-4 flex items-center gap-4 hover:transition-shadow">
      <div className={cn('w-2 h-10 rounded-full', color)} />
      <div>
        <div className="text-2xl font-semibold">{count}</div>
        <div className="text-xs text-muted-foreground font-medium tracking-tight">{label}</div>
      </div>
    </div>
  );
}
